import { useState, useCallback } from 'react';
import { Input, message } from 'antd';
import PSAIModal from '@PSAIComponents/PSAIModal';
import API from '@api/api';
import Iconfont from '@utils/Iconfont';

/**
 * 文件夹名称校验
 */
const validateFolderName = (name) => {
    const trimmed = name.trim();
    if (!trimmed) {
        return '请输入文件夹名称';
    }
    if (trimmed.length > 30) {
        return '文件夹名称不能超过30个字符';
    }
    const invalidChars = /[\\/:*?"<>|]/;
    if (invalidChars.test(trimmed)) {
        return '文件夹名称不能包含 \\ / : * ? " < > | 等特殊字符';
    }
    return '';
};

// 模式配置
const MODE_CONFIG = {
    create: { title: '新建文件夹', placeholder: '请输入文件夹名称', successMsg: '文件夹创建成功', failMsg: '创建失败' },
    rename: { title: '重命名', placeholder: '请输入文件夹名称', successMsg: '重命名成功', failMsg: '重命名失败' },
    renameRes: { title: '重命名参考图', placeholder: '请输入参考图名称', successMsg: '重命名成功', failMsg: '重命名失败' },
};

/**
 * 新建/重命名文件夹/重命名参考图弹窗组件
 * @param {boolean} open - 是否显示
 * @param {'create'|'rename'|'renameRes'} mode - 弹窗模式
 * @param {string} initialName - 初始名称（重命名时传入）
 * @param {string} parentId - 新建时的父级文件夹ID
 * @param {string} renameId - 重命名时的文件夹ID / 参考图ID
 * @param {string} scope - personal/enterprise
 * @param {function} onSuccess - 操作成功回调
 * @param {function} onCancel - 取消回调
 */
const FolderNameModal = ({ open, mode = 'create', initialName = '', parentId = '', renameId = '', scope = 'personal', zIndex, getContainer, onSuccess, onCancel }) => {
    const [folderName, setFolderName] = useState(initialName);
    const [folderNameError, setFolderNameError] = useState('');
    const [loading, setLoading] = useState(false);

    // open 变化时重置
    const handleAfterOpenChange = useCallback(() => {
        if (open) {
            setFolderName(initialName);
            setFolderNameError('');
        }
    }, [open, initialName]);

    const handleOk = () => {
        const error = validateFolderName(folderName);
        if (error) {
            setFolderNameError(error);
            return;
        }
        setLoading(true);

        const config = MODE_CONFIG[mode] || MODE_CONFIG.create;
        let apiCall;
        if (mode === 'renameRes') {
            apiCall = API.materialResRename({ resId: renameId, name: folderName.trim() });
        } else if (mode === 'rename') {
            apiCall = API.materialFolderRename({ folderId: renameId, name: folderName.trim() });
        } else {
            apiCall = API.materialFolderCreate({ name: folderName.trim(), scope, parentId });
        }

        apiCall
            .then((res) => {
                if (res.code === 0) {
                    message.success(config.successMsg);
                    onSuccess?.();
                } else {
                    message.error(res.message || config.failMsg);
                }
            })
            .catch(() => {
                message.error(config.failMsg + '，请稍后重试');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleCancel = () => {
        setFolderNameError('');
        onCancel?.();
    };

    return (
        <PSAIModal
            title={(MODE_CONFIG[mode] || MODE_CONFIG.create).title}
            open={open}
            onOk={handleOk}
            onCancel={handleCancel}
            maxWidth={400}
            confirmLoading={loading}
            afterOpenChange={handleAfterOpenChange}
            getContainer={getContainer}
            {...(zIndex != null ? { zIndex } : {})}
        >
            <Input
                prefix={<Iconfont type="icon-Material-EditOutlined" />}
                placeholder={(MODE_CONFIG[mode] || MODE_CONFIG.create).placeholder}
                value={folderName}
                maxLength={30}
                showCount
                status={folderNameError ? 'error' : undefined}
                onChange={(e) => {
                    setFolderName(e.target.value);
                    if (folderNameError) setFolderNameError('');
                }}
                onPressEnter={handleOk}
                style={{ width: 352, height: 36, margin: '36px 24px 0 24px' }}
            />
            {folderNameError && (
                <div style={{ color: '#ff4d4f', fontSize: 12, margin: '4px 24px 0 24px' }}>
                    {folderNameError}
                </div>
            )}
        </PSAIModal>
    );
};

export default FolderNameModal;
